import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import {
  AppAlert,
  AppAutocomplete,
  AppButton,
  AppFormField,
  AppInput,
  AppSpinner,
  AppTextarea,
} from '../../components/ui';
import { ROUTES } from '../../config/routes';
import { resolveAgendaMediaUrl } from '../../config/agendaApiConfig';
import agendaDatesService from '../../services/agendaDatesService';
import AgendaImageEditor from './AgendaImageEditor';
import AgendaMediaModal from './AgendaMediaModal';
import { formatSaisieLong, formatWhen, prixLabel } from './agendaDisplay';
import '../../styles/sites-web.scss';
import '../../styles/agenda-dates.scss';

const emptyForm = () => ({
  titre: '',
  statut: 0,
  dateDeDebut: '',
  dateDeFin: '',
  heureDeDebut: '',
  heureDeFin: '',
  prixMinimum: '',
  prixMaximum: '',
  nomDuLieu: '',
  adresse: '',
  communeId: null,
  communeNom: '',
  communeQuery: '',
  thematiqueIds: [],
  contenu: '',
  contenuIa: '',
  nom: '',
  prenom: '',
  societe: '',
  telephone: '',
  email: '',
  siteInternet: '',
  pageFacebook: '',
  informationsDeReservation: '',
  lienReservation: '',
  sourceDesInformations: '',
  image: '',
  copyrightDeImage: '',
  titreSeo: '',
  metaSeo: '',
  chatGPTAvecDate: '',
  chatGPTSansDate: '',
  packweb: false,
  nePasPublier: false,
  slug: '',
  dateSaisie: '',
  sourceImport: '',
});

const fromDate = (date) => ({
  ...emptyForm(),
  titre: date.titre || '',
  statut: date.statut ?? 0,
  dateDeDebut: date.dateDeDebut || '',
  dateDeFin: date.dateDeFin || '',
  heureDeDebut: date.heureDeDebut || '',
  heureDeFin: date.heureDeFin || '',
  prixMinimum: date.prixMinimum || '',
  prixMaximum: date.prixMaximum || '',
  nomDuLieu: date.nomDuLieu || '',
  adresse: date.adresse || '',
  communeId: date.commune?.id ?? null,
  communeNom: date.commune?.nom || '',
  communeQuery: date.commune ? `${date.commune.nom}${date.commune.codePostal ? ` (${date.commune.codePostal})` : ''}` : '',
  thematiqueIds: (date.thematiques || []).map((item) => item.id),
  contenu: date.contenu || '',
  contenuIa: date.contenuIa || '',
  nom: date.nom || '',
  prenom: date.prenom || '',
  societe: date.societe || '',
  telephone: date.telephone || '',
  email: date.email || '',
  siteInternet: date.siteInternet || '',
  pageFacebook: date.pageFacebook || '',
  informationsDeReservation: date.informationsDeReservation || '',
  lienReservation: date.lienReservation || '',
  sourceDesInformations: date.sourceDesInformations || '',
  image: date.image || '',
  copyrightDeImage: date.copyrightDeImage || '',
  titreSeo: date.titreSeo || '',
  metaSeo: date.metaSeo || '',
  chatGPTAvecDate: date.chatGPTAvecDate || '',
  chatGPTSansDate: date.chatGPTSansDate || '',
  packweb: Boolean(date.packweb),
  nePasPublier: Boolean(date.nePasPublier),
  slug: date.slug || '',
  dateSaisie: date.dateSaisie || '',
  sourceImport: date.sourceImport || '',
});

const toPayload = (form, { includeStatut }) => {
  const payload = {
    titre: form.titre,
    dateDeDebut: form.dateDeDebut || null,
    dateDeFin: form.dateDeFin || null,
    heureDeDebut: form.heureDeDebut || null,
    heureDeFin: form.heureDeFin || null,
    prixMinimum: form.prixMinimum,
    prixMaximum: form.prixMaximum,
    nomDuLieu: form.nomDuLieu,
    adresse: form.adresse,
    communeId: form.communeId,
    thematiqueIds: form.thematiqueIds,
    contenu: form.contenu,
    contenuIa: form.contenuIa,
    nom: form.nom,
    prenom: form.prenom,
    societe: form.societe,
    telephone: form.telephone,
    email: form.email,
    siteInternet: form.siteInternet,
    pageFacebook: form.pageFacebook,
    informationsDeReservation: form.informationsDeReservation,
    lienReservation: form.lienReservation,
    sourceDesInformations: form.sourceDesInformations,
    image: form.image,
    copyrightDeImage: form.copyrightDeImage,
    titreSeo: form.titreSeo,
    metaSeo: form.metaSeo,
    chatGPTAvecDate: form.chatGPTAvecDate,
    chatGPTSansDate: form.chatGPTSansDate,
    packweb: form.packweb,
    nePasPublier: form.nePasPublier,
    slug: form.slug,
  };
  if (includeStatut) payload.statut = Number(form.statut);
  return payload;
};

const aiContext = (form) => ({
  content: form.contenu,
  titre: form.titre,
  dateDeDebut: form.dateDeDebut,
  dateDeFin: form.dateDeFin,
  heureDeDebut: form.heureDeDebut,
  nomDuLieu: form.nomDuLieu,
  communeNom: form.communeNom,
});

const statutLabel = (statut) => {
  if (Number(statut) === 1) return 'Activée';
  if (Number(statut) === 99) return 'Supprimée';
  return 'Désactivée';
};

const CONTENT_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean'],
  ],
};

const CONTENT_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'blockquote', 'link'];

const plainText = (html) => String(html || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/\s+/g, ' ')
  .trim();

const SEO_FIELDS = [
  ['titreSeo', 'Titre SEO', 60, 'seo-title', 2],
  ['metaSeo', 'Meta description', 160, 'meta', 3],
  ['chatGPTAvecDate', 'Résumé avec date', 160, 'summary-with-date', 3],
  ['chatGPTSansDate', 'Résumé newsletter', 160, 'summary-without-date', 3],
];

const AgendaDateFormPage = () => {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromQueue = searchParams.get('from') === 'a-activer';
  const imageRef = useRef(null);
  const localPreviewRef = useRef('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [localPreview, setLocalPreview] = useState('');
  const [form, setForm] = useState(() => ({ ...emptyForm(), statut: isCreate ? 1 : 0 }));
  const [thematiques, setThematiques] = useState([]);
  const [themeQuery, setThemeQuery] = useState('');
  const [communes, setCommunes] = useState([]);
  const [communesLoading, setCommunesLoading] = useState(false);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [aiAction, setAiAction] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUseIa, setConfirmUseIa] = useState(false);
  const [saved, setSaved] = useState(false);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const replaceLocalPreview = (next) => {
    if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    localPreviewRef.current = next;
    setLocalPreview(next);
  };

  useEffect(() => () => {
    if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
  }, []);

  useEffect(() => {
    agendaDatesService.referentiels()
      .then((data) => setThematiques(data.thematiques || []))
      .catch(() => setError('Impossible de charger les thématiques.'));
  }, []);

  useEffect(() => {
    if (isCreate) {
      setForm({ ...emptyForm(), statut: 1 });
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setInfo('');
    agendaDatesService.get(id)
      .then((date) => {
        if (!cancelled) setForm(fromDate(date));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Date introuvable.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isCreate]);

  useEffect(() => {
    const query = form.communeQuery.trim();
    if (form.communeId || query.length < 2) {
      setCommunes([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      setCommunesLoading(true);
      agendaDatesService.communes(query)
        .then((items) => setCommunes(Array.isArray(items) ? items : []))
        .catch(() => setCommunes([]))
        .finally(() => setCommunesLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [form.communeQuery, form.communeId]);

  useEffect(() => {
    if (!saved) return undefined;
    const timer = setTimeout(() => setSaved(false), 1600);
    return () => clearTimeout(timer);
  }, [saved]);

  const missing = useMemo(() => {
    const gaps = [];
    if (!form.titre.trim()) gaps.push('Titre');
    if (!form.dateDeDebut) gaps.push('Date');
    if (!form.nomDuLieu.trim() && !form.communeId) gaps.push('Lieu');
    if (form.thematiqueIds.length === 0) gaps.push('Thématique');
    if (!form.contenu.replace(/<[^>]*>/g, '').trim()) gaps.push('Contenu');
    if (!form.titreSeo.trim() || !form.metaSeo.trim() || !form.chatGPTAvecDate.trim() || !form.chatGPTSansDate.trim()) {
      gaps.push('SEO');
    }
    return gaps;
  }, [form]);

  const backTo = fromQueue ? ROUTES.agenda.datesAActiver : ROUTES.agenda.dates;
  const imageUrl = localPreview || resolveAgendaMediaUrl(form.image);
  const imageName = form.image ? String(form.image).split('/').filter(Boolean).pop() : '';
  const when = formatWhen(form);
  const price = prixLabel(form.prixMinimum, form.prixMaximum);
  const contenuText = plainText(form.contenu);
  const iaText = plainText(form.contenuIa);
  const words = contenuText ? contenuText.split(' ').length : 0;
  const iaWords = iaText ? iaText.split(' ').length : 0;
  const seoFilled = SEO_FIELDS.filter(([key]) => String(form[key] || '').trim()).length;
  const filteredThemes = thematiques.filter((item) => item.nom.toLowerCase().includes(themeQuery.trim().toLowerCase()));

  const applyResult = (result, message) => {
    if (result?.date) setForm(fromDate(result.date));
    const warnings = result?.warnings || [];
    setInfo(warnings.length ? `${message} ${warnings.join(' ')}` : message);
    setError(warnings.length ? warnings.join(' ') : '');
  };

  const save = async (includeStatut) => {
    setSaving(true);
    setError('');
    setInfo('');
    setConfirmDelete(false);
    try {
      const payload = toPayload(form, { includeStatut });
      if (isCreate) {
        const result = await agendaDatesService.create({ ...payload, statut: Number(form.statut) });
        navigate(ROUTES.agenda.dateEdit(result.date.id), { replace: true });
        return;
      }
      const result = await agendaDatesService.update(id, payload);
      applyResult(result, 'Date enregistrée.');
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const activer = async () => {
    setSaving(true);
    setError('');
    setInfo('');
    setConfirmDelete(false);
    try {
      await agendaDatesService.update(id, toPayload(form, { includeStatut: false }));
      const result = await agendaDatesService.activer(id);
      if (fromQueue && result.nextId && String(result.nextId) !== String(id)) {
        navigate(`${ROUTES.agenda.dateEdit(result.nextId)}?from=a-activer`);
        return;
      }
      if (fromQueue) {
        navigate(ROUTES.agenda.datesAActiver);
        return;
      }
      applyResult(result, 'Date activée.');
    } catch (err) {
      setError(err.message || 'Activation impossible.');
    } finally {
      setSaving(false);
    }
  };

  const desactiver = async () => {
    setSaving(true);
    setError('');
    setInfo('');
    setConfirmDelete(false);
    try {
      const result = await agendaDatesService.update(id, { ...toPayload(form, { includeStatut: true }), statut: 0 });
      applyResult(result, 'Date désactivée.');
    } catch (err) {
      setError(err.message || 'Désactivation impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    try {
      await agendaDatesService.update(id, { statut: 99 });
      navigate(backTo);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setConfirmDelete(false);
      setSaving(false);
    }
  };

  const runAi = async (action) => {
    setAiAction(action);
    setError('');
    try {
      const result = await agendaDatesService.ai(action, aiContext(form));
      if (action === 'improve-content' && result.content) {
        setField('contenuIa', result.content);
        setConfirmUseIa(false);
      }
      if (action === 'seo-title' && result.titreSeo) setField('titreSeo', result.titreSeo);
      if (action === 'meta' && result.metaSeo) setField('metaSeo', result.metaSeo);
      if (action === 'summary-with-date' && result.chatGPTAvecDate) setField('chatGPTAvecDate', result.chatGPTAvecDate);
      if (action === 'summary-without-date' && result.chatGPTSansDate) setField('chatGPTSansDate', result.chatGPTSansDate);
      if (action === 'fill-empty') {
        setForm((current) => ({
          ...current,
          titreSeo: current.titreSeo.trim() ? current.titreSeo : (result.titreSeo || ''),
          metaSeo: current.metaSeo.trim() ? current.metaSeo : (result.metaSeo || ''),
          chatGPTAvecDate: current.chatGPTAvecDate.trim() ? current.chatGPTAvecDate : (result.chatGPTAvecDate || ''),
          chatGPTSansDate: current.chatGPTSansDate.trim() ? current.chatGPTSansDate : (result.chatGPTSansDate || ''),
        }));
      }
    } catch (err) {
      setError(err.message || 'La génération a échoué.');
    } finally {
      setAiAction('');
    }
  };

  const useIaVersion = () => {
    if (!plainText(form.contenuIa)) return;
    if (!confirmUseIa) {
      setConfirmUseIa(true);
      return;
    }
    setField('contenu', form.contenuIa);
    setConfirmUseIa(false);
  };

  const toggleTheme = (themeId) => {
    setForm((current) => {
      const has = current.thematiqueIds.includes(themeId);
      return {
        ...current,
        thematiqueIds: has
          ? current.thematiqueIds.filter((value) => value !== themeId)
          : [...current.thematiqueIds, themeId],
      };
    });
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    replaceLocalPreview(URL.createObjectURL(file));
    setUploadingImage(true);
    setError('');
    try {
      const result = await agendaDatesService.uploadMedia(file);
      setField('image', result.path || '');
      replaceLocalPreview('');
    } catch (err) {
      setError(err.message || 'Téléversement impossible.');
    } finally {
      setUploadingImage(false);
    }
  };

  const active = Number(form.statut) === 1;

  return (
    <MasterLayout>
      <div className="sites-web agenda-dates">
        <div className="ag-sticky">
          <div className="ag-crumb">
            <AppButton as={Link} to={backTo} className="sw-btn ag-back" variant="secondary" icon="lucide:arrow-left" iconWidth={16} aria-label="Retour à la liste" />
            <div className="ag-crumb-text">
              <strong>
                <Link to={backTo}>{queueLabel(fromQueue)}</Link>
                <Icon icon="lucide:chevron-right" width={14} />
                {isCreate ? 'Nouvelle date' : `#${id}`}
                {isCreate ? null : <em className={active ? 'ag-badge ag-badge--live' : 'ag-badge ag-badge--off'}>{statutLabel(form.statut)}</em>}
              </strong>
              <span className="ag-hint">{missing.length ? `Il manque : ${missing.join(', ')}` : 'Prête à être publiée'}</span>
            </div>
          </div>
          <div className="ag-actions-bar">
            {isCreate ? null : (
              <AppButton
                className={`sw-btn${confirmDelete ? ' ag-btn-danger' : ''}`}
                variant="secondary"
                disabled={saving}
                onClick={remove}
              >
                {confirmDelete ? 'Confirmer la suppression' : 'Supprimer'}
              </AppButton>
            )}
            <AppButton className="sw-btn" variant="secondary" icon={saved ? 'lucide:check' : undefined} iconWidth={16} loading={saving} onClick={() => save(true)}>
              {saved ? 'Enregistré' : 'Enregistrer'}
            </AppButton>
            {isCreate ? null : (
              <AppButton
                className={active ? 'sw-btn' : 'sw-btn sw-btn--dark'}
                variant="secondary"
                disabled={saving}
                onClick={active ? desactiver : activer}
              >
                {active ? 'Désactiver' : 'Activer'}
              </AppButton>
            )}
          </div>
        </div>

        {error ? <AppAlert variant="danger">{error}</AppAlert> : null}
        {info && !error ? <AppAlert variant="success">{info}</AppAlert> : null}

        {loading ? <AppSpinner /> : (
          <>
            <div className="ag-title-row">
              <h1 className="fw-semibold mb-0 app-page-title">{form.titre.trim() || 'Nouvelle date'}</h1>
            </div>
            <div className="ag-edit">
              <div className="ag-stack">
                <section className="ag-card">
                  <h2><Icon icon="lucide:type" width={16} /> Titre</h2>
                  <AppFormField label="Titre" htmlFor="agenda-titre" required className="ag-field">
                    <AppInput id="agenda-titre" value={form.titre} onChange={(event) => setField('titre', event.target.value)} />
                  </AppFormField>
                </section>

                <section className="ag-card">
                  <h2><Icon icon="lucide:calendar" width={16} /> Quand</h2>
                  <div className="ag-4">
                    <AppFormField label="Début" htmlFor="agenda-debut" required className="ag-field">
                      <AppInput id="agenda-debut" type="date" value={form.dateDeDebut} onChange={(event) => setField('dateDeDebut', event.target.value)} />
                    </AppFormField>
                    <AppFormField label="Fin" htmlFor="agenda-fin" className="ag-field">
                      <AppInput id="agenda-fin" type="date" value={form.dateDeFin} onChange={(event) => setField('dateDeFin', event.target.value)} />
                    </AppFormField>
                    <AppFormField label="Heure" htmlFor="agenda-hdebut" className="ag-field">
                      <AppInput id="agenda-hdebut" type="time" value={form.heureDeDebut} onChange={(event) => setField('heureDeDebut', event.target.value)} />
                    </AppFormField>
                    <AppFormField label="Fin" htmlFor="agenda-hfin" className="ag-field">
                      <AppInput id="agenda-hfin" type="time" value={form.heureDeFin} onChange={(event) => setField('heureDeFin', event.target.value)} />
                    </AppFormField>
                  </div>
                  {when ? <p className="ag-note">Affiché : {when}</p> : null}
                </section>

                <section className="ag-card">
                  <h2><Icon icon="lucide:map-pin" width={16} /> Où</h2>
                  <AppFormField label="Lieu" htmlFor="agenda-lieu" className="ag-field">
                    <AppInput id="agenda-lieu" value={form.nomDuLieu} onChange={(event) => setField('nomDuLieu', event.target.value)} />
                  </AppFormField>
                  <div className="ag-2">
                    <AppFormField label="Adresse" htmlFor="agenda-adresse" className="ag-field">
                      <AppInput id="agenda-adresse" value={form.adresse} onChange={(event) => setField('adresse', event.target.value)} />
                    </AppFormField>
                    <AppFormField label="Commune" htmlFor="agenda-commune" className="ag-field">
                      <AppAutocomplete
                        value={form.communeQuery}
                        onChange={(value) => setForm((current) => ({
                          ...current,
                          communeQuery: value,
                          communeId: null,
                          communeNom: '',
                        }))}
                        items={communes}
                        loading={communesLoading}
                        getItemKey={(item) => item.id}
                        renderItem={(item) => `${item.nom}${item.codePostal ? ` (${item.codePostal})` : ''}`}
                        onSelect={(item) => setForm((current) => ({
                          ...current,
                          communeId: item.id,
                          communeNom: item.nom,
                          communeQuery: `${item.nom}${item.codePostal ? ` (${item.codePostal})` : ''}`,
                        }))}
                        placeholder="Nom ou code postal"
                      />
                    </AppFormField>
                  </div>
                </section>

                <section className="ag-card">
                  <h2><Icon icon="lucide:ticket" width={16} /> Prix</h2>
                  <div className="ag-2">
                    <AppFormField label="Minimum" htmlFor="agenda-pmin" className="ag-field">
                      <AppInput id="agenda-pmin" value={form.prixMinimum} onChange={(event) => setField('prixMinimum', event.target.value)} />
                    </AppFormField>
                    <AppFormField label="Maximum" htmlFor="agenda-pmax" className="ag-field">
                      <AppInput id="agenda-pmax" value={form.prixMaximum} onChange={(event) => setField('prixMaximum', event.target.value)} />
                    </AppFormField>
                  </div>
                  <p className="ag-note">Affiché sur le site : {price.text}</p>
                </section>

                <section className="ag-card">
                  <div className="ag-card-head">
                    <h2><Icon icon="lucide:tags" width={16} /> Thématiques</h2>
                    <span className="ag-count-text">{form.thematiqueIds.length} sélectionnée{form.thematiqueIds.length > 1 ? 's' : ''}</span>
                  </div>
                  <AppInput value={themeQuery} onChange={(event) => setThemeQuery(event.target.value)} placeholder="Filtrer les thématiques" aria-label="Filtrer les thématiques" />
                  <div className="ag-pills">
                    {filteredThemes.map((item) => {
                      const selected = form.thematiqueIds.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`ag-pill${selected ? ' is-on' : ''}`}
                          aria-pressed={selected}
                          onClick={() => toggleTheme(item.id)}
                        >
                          {item.nom}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="ag-card">
                  <h2><Icon icon="lucide:align-left" width={16} /> Contenu</h2>
                  <div className="ag-compare">
                    <div className="ag-compare__col">
                      <div className="ag-inline">
                        <span className="ag-compare__label">Texte déposé</span>
                        <span className="ag-count-text">{words} mot{words > 1 ? 's' : ''}</span>
                      </div>
                      <ReactQuill
                        className="ag-editor"
                        theme="snow"
                        value={form.contenu || ''}
                        onChange={(html) => setField('contenu', plainText(html) ? html : '')}
                        modules={CONTENT_MODULES}
                        formats={CONTENT_FORMATS}
                        placeholder="Texte saisi lors du dépôt"
                      />
                    </div>
                    <div className="ag-compare__col">
                      <div className="ag-inline">
                        <span className="ag-compare__label">Proposition IA</span>
                        <span className="ag-count-text">{iaWords} mot{iaWords > 1 ? 's' : ''}</span>
                      </div>
                      <ReactQuill
                        className="ag-editor"
                        theme="snow"
                        value={form.contenuIa || ''}
                        onChange={(html) => {
                          setConfirmUseIa(false);
                          setField('contenuIa', plainText(html) ? html : '');
                        }}
                        modules={CONTENT_MODULES}
                        formats={CONTENT_FORMATS}
                        placeholder="La proposition s’affiche ici, sans remplacer le texte déposé"
                      />
                      <div className="ag-inline">
                        <AppButton
                          className="sw-btn"
                          variant="secondary"
                          icon="lucide:sparkles"
                          iconWidth={16}
                          loading={aiAction === 'improve-content'}
                          disabled={!contenuText || Boolean(aiAction)}
                          onClick={() => runAi('improve-content')}
                        >
                          Améliorer avec l’IA
                        </AppButton>
                        <AppButton
                          className="sw-btn sw-btn--page"
                          variant="secondary"
                          disabled={!iaText || Boolean(aiAction)}
                          onClick={useIaVersion}
                        >
                          {confirmUseIa ? 'Confirmer le remplacement' : 'Reprendre dans le texte déposé'}
                        </AppButton>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="ag-card">
                  <div className="ag-card-head">
                    <h2><Icon icon="lucide:search" width={16} /> SEO et résumés</h2>
                    <span className="ag-count-text">{seoFilled}/4</span>
                  </div>
                  <div className="ag-progress" aria-hidden><span style={{ width: `${(seoFilled / 4) * 100}%` }} /></div>
                  <div>
                    <AppButton
                      className="sw-btn"
                      variant="secondary"
                      icon="lucide:sparkles"
                      iconWidth={16}
                      loading={aiAction === 'fill-empty'}
                      disabled={!contenuText || Boolean(aiAction)}
                      onClick={() => runAi('fill-empty')}
                    >
                      Générer ce qui est vide
                    </AppButton>
                  </div>
                  {SEO_FIELDS.map(([key, label, max, action, rows]) => {
                    const length = Array.from(String(form[key] || '')).length;
                    const ratio = Math.min(100, max ? (length / max) * 100 : 0);
                    return (
                      <div className="ag-seo" key={key}>
                        <div className="ag-inline">
                          <span className="ag-count-text">{label}</span>
                          <span className="ag-count-text">{length} / {max}</span>
                        </div>
                        {rows > 2 ? (
                          <AppTextarea id={`agenda-${key}`} rows={rows} value={form[key]} onChange={(event) => setField(key, event.target.value)} aria-label={label} />
                        ) : (
                          <AppInput id={`agenda-${key}`} value={form[key]} onChange={(event) => setField(key, event.target.value)} aria-label={label} />
                        )}
                        <div className={`ag-meter${length > max ? ' is-over' : ''}`} aria-hidden><span style={{ width: `${ratio}%` }} /></div>
                        <div>
                          <AppButton
                            className="sw-btn sw-btn--page"
                            variant="secondary"
                            loading={aiAction === action}
                            disabled={!contenuText || Boolean(aiAction)}
                            onClick={() => runAi(action)}
                          >
                            Régénérer
                          </AppButton>
                        </div>
                      </div>
                    );
                  })}
                </section>
              </div>

              <aside className="ag-stack">
                <section className="ag-card">
                  <p className="ag-kicker">DIFFUSION</p>
                  <div className="ag-toggle">
                    <div>
                      <strong>Pack web</strong>
                      <span>Visible sur le site vitrine</span>
                    </div>
                    <button type="button" className={`ag-switch${form.packweb ? ' is-on' : ''}`} aria-pressed={form.packweb} onClick={() => setField('packweb', !form.packweb)}>
                      <i />
                    </button>
                  </div>
                  <div className="ag-toggle">
                    <div>
                      <strong>Ne pas publier sur Facebook</strong>
                      <span>Exclure cette date du réseau</span>
                    </div>
                    <button type="button" className={`ag-switch${form.nePasPublier ? ' is-on' : ''}`} aria-pressed={form.nePasPublier} onClick={() => setField('nePasPublier', !form.nePasPublier)}>
                      <i />
                    </button>
                  </div>
                </section>

                <section className="ag-card">
                  <p className="ag-kicker">MÉDIAS</p>
                  {imageUrl ? (
                    <button type="button" className="ag-media ag-media--button" onClick={() => setEditorOpen(true)}>
                      <img src={imageUrl} alt="" />
                      <span>Recadrer</span>
                    </button>
                  ) : (
                    <div className="ag-media"><span>Aucune image</span></div>
                  )}
                  {imageName ? <p className="ag-meta" title={form.image}>{imageName}</p> : null}
                  <input
                    ref={imageRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    hidden
                    onChange={uploadImage}
                  />
                  <div className="ag-inline">
                    <AppButton
                      className="sw-btn sw-btn--page"
                      variant="secondary"
                      icon="lucide:upload"
                      iconWidth={16}
                      loading={uploadingImage}
                      onClick={() => imageRef.current?.click()}
                    >
                      Téléverser
                    </AppButton>
                    <AppButton
                      className="sw-btn sw-btn--page"
                      variant="secondary"
                      icon="lucide:images"
                      iconWidth={16}
                      onClick={() => setLibraryOpen(true)}
                    >
                      Bibliothèque
                    </AppButton>
                    {form.image ? (
                      <AppButton className="sw-btn sw-btn--page" variant="secondary" onClick={() => setField('image', '')}>
                        Retirer
                      </AppButton>
                    ) : null}
                  </div>
                  <AppFormField label="Copyright" htmlFor="agenda-copyright" className="ag-field">
                    <AppInput id="agenda-copyright" value={form.copyrightDeImage} onChange={(event) => setField('copyrightDeImage', event.target.value)} />
                  </AppFormField>
                  <AgendaMediaModal
                    show={libraryOpen}
                    onClose={() => setLibraryOpen(false)}
                    onSelect={(path) => {
                      replaceLocalPreview('');
                      setField('image', path);
                      setLibraryOpen(false);
                    }}
                  />
                  <AgendaImageEditor
                    show={editorOpen}
                    path={form.image}
                    src={localPreview}
                    fileName={imageName}
                    canApply={!localPreview}
                    onClose={() => setEditorOpen(false)}
                    onApply={(nextPath) => {
                      if (nextPath) setField('image', nextPath);
                      setEditorOpen(false);
                    }}
                  />
                </section>

                <section className="ag-card">
                  <p className="ag-kicker">CONTACT</p>
                  {[
                    ['prenom', 'Prénom', 'text'],
                    ['nom', 'Nom', 'text'],
                    ['email', 'E-mail', 'email'],
                    ['telephone', 'Téléphone', 'tel'],
                    ['societe', 'Société', 'text'],
                    ['lienReservation', 'Réservation', 'url'],
                    ['siteInternet', 'Site', 'url'],
                    ['pageFacebook', 'Facebook', 'url'],
                  ].map(([key, label, type]) => (
                    <AppFormField key={key} label={label} htmlFor={`agenda-${key}`} className="ag-field">
                      <AppInput id={`agenda-${key}`} type={type} value={form[key]} onChange={(event) => setField(key, event.target.value)} />
                    </AppFormField>
                  ))}
                  <AppFormField label="Informations de réservation" htmlFor="agenda-infos" className="ag-field">
                    <AppTextarea id="agenda-infos" rows={3} value={form.informationsDeReservation} onChange={(event) => setField('informationsDeReservation', event.target.value)} />
                  </AppFormField>
                  <AppFormField label="Source des informations" htmlFor="agenda-source" className="ag-field">
                    <AppTextarea id="agenda-source" rows={2} value={form.sourceDesInformations} onChange={(event) => setField('sourceDesInformations', event.target.value)} />
                  </AppFormField>
                  {form.dateSaisie || form.sourceImport ? (
                    <p className="ag-meta">
                      {form.dateSaisie ? `Saisie le ${formatSaisieLong(form.dateSaisie)}` : 'Saisie'}
                      {form.sourceImport ? ` · ${form.sourceImport}` : ''}
                    </p>
                  ) : null}
                </section>
              </aside>
            </div>
          </>
        )}
      </div>
    </MasterLayout>
  );
};

const queueLabel = (fromQueue) => (fromQueue ? 'Dates à activer' : 'Dates');

export default AgendaDateFormPage;
